import { Cog, Search, Target, Lightbulb, Rocket } from "lucide-react";
import { useInView } from "@/hooks/useInView";
import doubleDiamondImage from "@/assets/double-diamond.webp";

const phases = [
  {
    id: "discover",
    title: "Discover",
    description: "Problem exploration, user research, competition analysis, and user journey mapping.",
    items: ["Problem Exploration", "User Research", "Competition Analysis", "User Journey Map"],
  },
  {
    id: "define",
    title: "Define",
    description: "Organize research, create user profiles, prioritize features, and define requirements.",
    items: ["Organizing Research", "User Profiles", "Prioritizing Features", "Defining Requirements"],
  },
  {
    id: "develop",
    title: "Develop",
    description: "Explore design ideas, create wireframes, user flows, and gather feedback.",
    items: ["Design Ideas", "Information Architecture", "User Flows", "Testing & Feedback"],
  },
  {
    id: "deliver",
    title: "Deliver",
    description: "Build prototypes, test with users, iterate, and launch the final product.",
    items: ["Creating Prototypes", "User Testing", "Making Adjustments", "Launch Product"],
  },
];

export const DoubleDiamondProcess = () => {
  const { ref, isInView } = useInView({ threshold: 0.1 });

  return (
    <section ref={ref} className="py-12 md:py-20 lg:py-24 bg-muted/30 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            <Cog className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>Double Diamond Process</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 sm:mb-4 px-2">
            How We Build Your Software
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-3xl mx-auto px-2">
            We follow the proven Double Diamond methodology — diverging to explore possibilities, then converging to deliver focused solutions.
          </p>
        </div>

        {/* Double Diamond Image */}
        <div 
          className={`relative max-w-5xl mx-auto mb-8 sm:mb-12 md:mb-16 transition-all duration-1000 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-8 shadow-lg">
            <img 
              src={doubleDiamondImage} 
              alt="4 Phases of Double Diamond Model: Discover, Define, Develop, and Deliver - showing the Strategic Phase and Creative Phase" 
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
        </div>

        {/* Phase Details Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 max-w-6xl mx-auto">
          {phases.map((phase, index) => {
            const icons = [Search, Target, Lightbulb, Rocket];
            const Icon = icons[index];
            const gradients = [
              "from-red-500 to-orange-500",
              "from-orange-500 to-amber-500",
              "from-blue-500 to-cyan-500",
              "from-cyan-500 to-emerald-500",
            ];

            return (
              <div
                key={phase.id}
                className={`group p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl bg-card border border-border hover:shadow-lg transition-all duration-500 ${
                  isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${300 + index * 100}ms` }}
              >
                <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${gradients[index]} flex items-center justify-center mb-2 sm:mb-3 md:mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/10 text-primary font-bold text-[10px] sm:text-xs">
                    {index + 1}
                  </span>
                  <h3 className="text-sm sm:text-base md:text-lg font-bold text-foreground">{phase.title}</h3>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 md:mb-4 hidden sm:block">{phase.description}</p>
                <ul className="space-y-1 sm:space-y-1.5 md:space-y-2">
                  {phase.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs md:text-sm text-muted-foreground">
                      <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-gradient-to-br ${gradients[index]} flex-shrink-0`}></div>
                      <span className="truncate sm:whitespace-normal">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
